import { apiClient } from './api-client'

export interface CartItemResponse {
  id: string
  product_id: number
  product_name: string
  supplier_product_code: string
  supplier_id: number
  supplier_name: string
  branch_id: string
  branch_name: string
  branch_is_active?: boolean
  branch_address_line1?: string
  branch_address_line2?: string
  branch_city?: string
  branch_postcode?: string
  branch_phone?: string
  quantity: number
  quantity_multiple?: number  // Multiple the product is sold in (1 = any qty)
  price_at_added: number | string  // Backend sends as string (Decimal)
  current_price: number | string    // Backend sends as string (Decimal)
  price_changed: boolean
  unit_of_measure: string
  image_url: string | null
  master_product_id: number | null
  distance_miles: number | null
  is_preferred: boolean
  is_restricted: boolean
  is_van_stock: boolean
  is_van_stock_toggled: boolean | null
  // Persisted borrowed delivery source — null when delivery is from the item's
  // own branch. Cart page fires the .../delivery-source/ PATCH after live
  // stock hydrates. Name comes via JOIN (backend); distance is postcode-
  // dependent so it stays in the live-stock response (same as own branch).
  delivery_source_branch_id: string | null
  delivery_source_branch_name: string | null
  // Engineer's fulfillment choice, persisted on the CartItem row so it
  // survives across tabs, refreshes, and devices. Null on rows created
  // before this field existed or when no explicit choice was sent — the
  // frontend keeps its "pickup unless delivery-only" default for that case.
  fulfillment_method: 'pickup' | 'delivery' | null
  // DEV-51011 AC13 — from adapters.registry.get_preferred_date_capability.
  supports_preferred_date?: boolean
  preferred_date_max_working_days?: number | null
  subtotal: number
  created_at: string
  updated_at: string
}

export interface CartResponse {
  id: string
  postcode: string
  items: CartItemResponse[]
  total_items?: number
  total_amount?: number
  created_at: string
  updated_at: string
}

export interface AddToCartRequest {
  product_id: number
  branch_id: string
  quantity: number
  // Optional so pre-existing callers keep working; ProductDetail sends the
  // engineer's PDP-side pickup/delivery choice through this field.
  fulfillment_method?: 'pickup' | 'delivery'
  // Quick add to cart: add on top of an existing line for the same product +
  // branch instead of replacing its quantity. Omitted = replace (default).
  add_to_existing?: boolean
  // price removed - security: backend fetches from catalogue_price
}

export interface UpdateCartItemRequest {
  quantity?: number
  is_van_stock_toggled?: boolean | null
  /** Move the line to a different branch of the same supplier (product detail
   * page branch selector). */
  branch_id?: string
  /** BM-014 / BM-015: engineer flipped pickup/delivery from the cart page. */
  fulfillment_method?: 'pickup' | 'delivery'
}

export interface SetPostcodeRequest {
  postcode: string
}

export interface UpdateCartItemDeliverySourceRequest {
  delivery_source_branch_id: string | null
}

export interface SetPostcodeResponse {
  message: string
  postcode: string
  /** True only when the engineer moved — false on a first-time set or a
   *  same-postcode resubmit, so those cases stay silent. */
  location_changed: boolean
  /** Lines kept in the basket across the change (DEV-51046). */
  item_count: number
}

/**
 * Get current user's cart
 */
export async function getCart(): Promise<CartResponse> {
  return apiClient.get<CartResponse>('/api/cart/')
}

/**
 * Add item to cart
 */
export async function addToCart(data: AddToCartRequest): Promise<CartItemResponse> {
  return apiClient.post<CartItemResponse>('/api/cart/items/', data)
}

/**
 * Update cart item quantity
 */
export async function updateCartItem(itemId: string, data: UpdateCartItemRequest): Promise<CartItemResponse> {
  return apiClient.patch<CartItemResponse>(`/api/cart/items/${itemId}/`, data)
}

/**
 * Remove item from cart
 */
export async function removeCartItem(itemId: string): Promise<void> {
  return apiClient.delete<void>(`/api/cart/items/${itemId}/`)
}

/**
 * Clear entire cart
 */
export async function clearCart(): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>('/api/cart/clear/')
}

/**
 * Set or update cart postcode
 */
export async function setCartPostcode(data: SetPostcodeRequest): Promise<SetPostcodeResponse> {
  return apiClient.post<SetPostcodeResponse>('/api/cart/set-postcode/', data)
}

/** A line to merge, with the borrowed delivery branch live stock reported. */
export interface MergeCartLineRequest {
  item_id: string
  delivery_source_branch_id: string | null
}

export interface MergeCartLinesResponse {
  item: CartItemResponse
  removed_item_ids: string[]
}

/**
 * Merge delivery lines of one product that the supplier delivers from the
 * same branch. The server checks they really share it, keeps the oldest line
 * with the summed quantity and deletes the others.
 */
export async function mergeCartLines(
  lines: MergeCartLineRequest[],
): Promise<MergeCartLinesResponse> {
  return apiClient.post<MergeCartLinesResponse>('/api/cart/items/merge/', { lines })
}

/**
 * Persist the borrowed delivery source resolved from live stock. Fire-and-forget
 * — called by the cart page after live stock hydrates so the next open groups
 * correctly on the very first render (no flicker).
 */
export async function updateCartItemDeliverySource(
  itemId: string,
  data: UpdateCartItemDeliverySourceRequest,
): Promise<CartItemResponse> {
  return apiClient.patch<CartItemResponse>(
    `/api/cart/items/${itemId}/delivery-source/`,
    data,
  )
}
